/**
 * The base font size in pixels used as the root HTML font-size.
 * All rem values in the configuration are relative to this value.
 *
 * @remarks
 * The default browser font size is 16px. Setting this value to 10 makes rem math trivial
 * (e.g., 1rem = 13px, 16px = 1.23rem, 160px = 12.3rem, etc.), but here it is set to 13px
 * for design consistency.
 *
 * @public
 */
export const BASE_FONT_SIZE_PX = 13;

/**
 * The base color for HSP's brand palette, used to generate the full range of brand colors.
 *
 * @remarks
 * This color is used as the input to {@link generateColorScale} to produce the full set of brand colors.
 *
 * @public
 */
export const BASE_HSP_COLOR = '#1d5e79';

/**
 * The base unit in rem, calculated as the ratio of {@link BASE_FONT_SIZE_PX} to the default browser font size (16px).
 *
 * @remarks
 * This value is used to convert pixel values to rem units relative to the base font size.
 *
 * @public
 */
export const BASE_UNIT_REM = BASE_FONT_SIZE_PX / 16;

/**
 * Converts a pixel value to a rem string relative to {@link BASE_FONT_SIZE_PX}.
 *
 * @param px - The pixel value to convert.
 * @returns The corresponding rem value as a string (e.g., '1.6rem').
 *
 * @example
 * ```typescript
 * rem(160); // returns '12.307692307692308rem' when BASE_FONT_SIZE_PX is 13
 * ```
 *
 * @public
 */
export const rem = (px: number): string => {
  return `${px / BASE_FONT_SIZE_PX}rem`;
};
